import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const DEFAULT_QUESTIONS = [
  { key: "property", question: "Will you acquire real estate?" },
  { key: "business", question: "Will you establish a business?" },
  { key: "family", question: "Are family dependents joining you?" },
];

// Pre-journey qualification questions. Answers branch the workflow.
export default function QualificationStep({ country, onClose, onBuild, building }) {
  const questions = useMemo(
    () => (Array.isArray(country?.questions) && country.questions.length ? country.questions : DEFAULT_QUESTIONS),
    [country]
  );
  const [answers, setAnswers] = useState(() => {
    const base = { family: true };
    questions.forEach((q) => { if (!(q.key in base)) base[q.key] = false; });
    return base;
  });

  return (
    <AnimatePresence>
      {country && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            data-testid="qualification-sheet"
            className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-lg rounded-t-3xl border-t border-white/10"
            style={{ background: "rgba(10,14,23,0.98)", boxShadow: "0 -20px 60px rgba(0,0,0,0.6)" }}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            <div className="flex justify-center pt-3"><div className="h-1.5 w-12 rounded-full bg-white/20" /></div>
            <div className="px-5 pb-6 pt-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/90">Your Objective</p>
                  <h2 className="mt-1 flex items-center gap-2 text-xl font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
                    <span className="text-2xl">{country.flag}</span> {country.objective || country.name}
                  </h2>
                  <p className="mt-1 text-xs text-slate-300">{country.program}</p>
                </div>
                <button data-testid="qualification-close-btn" onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 text-slate-300"><X size={16} /></button>
              </div>

              <p className="mt-5 text-[11px] font-medium uppercase tracking-wider text-slate-400">Tailor your pathway</p>
              <p className="mt-1 text-xs text-slate-400">A few quick questions so we build the right journey for you.</p>

              <div className="mt-3 space-y-2.5">
                {questions.map((q) => (
                  <div key={q.key} data-testid={`qualification-q-${q.key}`} className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.03] p-3.5">
                    <span className="pr-3 text-sm text-slate-200">{q.question}</span>
                    <Switch
                      data-testid={`qualification-toggle-${q.key}`}
                      checked={!!answers[q.key]}
                      onCheckedChange={(v) => setAnswers((a) => ({ ...a, [q.key]: v }))}
                    />
                  </div>
                ))}
              </div>

              <button
                data-testid="qualification-build-btn"
                disabled={building}
                onClick={() => onBuild(answers)}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-300 py-3.5 text-sm font-semibold text-[#07090E] transition-transform active:scale-[0.98] disabled:opacity-60"
              >
                {building ? <Loader2 size={16} className="animate-spin" /> : null}
                {building ? "Building your journey…" : "Build My Journey"}
                {!building && <ArrowRight size={17} />}
              </button>
              <p className="mt-3 text-center text-[11px] text-slate-400">
                For details about travel information, please discuss with your Consultant.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
